import { FormikValues } from 'formik';
import { TFunction } from 'i18next';
import React, { FC, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { LocationType } from '../../../../../../@posturinn/client-react';
import {
	OPTIONAL_SERVICE_COD,
	OPTIONAL_SERVICE_INSURANCE,
} from '../../../../../../constants/data/registerShipmentData';
import { fadeIn } from '../../../../../../constants/keyframes/fades';
import theme from '../../../../../../constants/theme';
import { getCurrentLangPretty } from '../../../../../../helpers/url/urlHelpers';
import useCountries from '../../../../../../hooks/useCountries';
import IPostService from '../../../../../../interfaces/forms/postService';
import { IStore } from '../../../../../../reducers';
import { IRegisterShipmentReducer } from '../../../../../../reducers/registerShipmentReducer';
import FormikCheckbox from '../../../../../base/forms/formikCheckbox';
import { SimpleFormikCheckboxTile } from '../../../../../base/forms/formikCheckboxTile/formikCheckboxTile';
import FormikCheckboxTileGroup from '../../../../../base/forms/formikCheckboxTile/formikCheckboxTileGroup';
import FormikFieldWrapper from '../../../../../base/forms/formikFieldWrapper';
import FormikRadioTile from '../../../../../base/forms/formikRadioTile/formikRadioTile';
import FormikSelect from '../../../../../base/forms/formikSelect';
import PhoneCountryInput from '../../../../../base/forms/phoneCountryInput';
import { Column, Columns } from '../../../../../base/grid';
import FormikRadioTileLoader from '../../../../../base/misc/formikRadioTileLoader';
import { A } from '../../../../../base/typography';
import { IStepInfoFormInfo } from '../../../stepInfo/stepInfo.schema';
import CodForm from '../../../stepOptionalServices/stepOptionalServicesForm/components/codForm';
import InsuranceForm from '../../../stepOptionalServices/stepOptionalServicesForm/components/insuranceForm';
import RecipientPaysSocialIdPrompt from '../../../stepOptionalServices/stepOptionalServicesForm/components/recipientPaysSocialIdPrompt';
import { getIconByServiceCode, getLocationsSelectOptions } from '../helpers';

interface Props {
	services: IPostService[];
	loading: boolean;
	values: FormikValues;
	setFieldTouched: any;
	setFieldValue: any;
	errors: any;
	parcelCount?: number;
	weightCategoryLabel?: string;
}

const requiresAuthentication = (extraService: IPostService['extraServices'][number]) =>
	extraService.requirements === 'RequiresAuthentication';

const sortOptionalServices = (extraServices: IPostService['extraServices']) =>
	[...extraServices].sort(
		(a, b) => Number(requiresAuthentication(a)) - Number(requiresAuthentication(b))
	);

const StyledService = styled.div`
	display: block;
	width: 100%;
	margin-bottom: 2rem;
	animation: ${fadeIn} 400ms linear;
`;

const StyledDescription = styled.div``;

const StyledNoServicesFound = styled.div`
	display: flex;
	justify-content: center;
	margin: 6rem 0;
	font-size: 2rem;
`;

const OptionalServices = styled.div`
	margin-top: 2rem;
	font-family: ${({ theme }) => theme.fonts.ArimoMedium};
	div:first-child {
		color: ${theme.colors.black};
		margin-bottom: 1rem;
	}
`;

const OptionalServicesForms = styled.div`
	border: 1px dashed ${({ theme }) => theme.colors.grayLight};
	margin-top: 3rem;
	padding: 2rem;

	p {
		margin-bottom: 3rem;
	}
`;

const Services: FC<Props> = ({ services, loading, values, setFieldTouched, setFieldValue, parcelCount = null, weightCategoryLabel }) => {
	const { t } = useTranslation('registerShipment') as unknown as { t: TFunction };
	const lang = getCurrentLangPretty();
	const { countries } = useCountries(lang, true);
	const { insuranceOptionalServiceCustomPrice, codOptionalServiceCustomPrice } = useSelector(
		({ registerShipmentReducer }) => registerShipmentReducer
	);

	const registerShipmentReducer: IRegisterShipmentReducer = useSelector<IStore, IRegisterShipmentReducer>(
		store => store.registerShipmentReducer
	);

	const infoFormInfo: IStepInfoFormInfo = registerShipmentReducer.stepInfoFormInfo;

	const postBoxesSelectOptions = getLocationsSelectOptions(
		services &&
			services[services.findIndex(service => service.id.toLowerCase() === LocationType.PostBox.toLowerCase())]
			? services[services.findIndex(service => service.id.toLowerCase() === LocationType.PostBox.toLowerCase())]
					.deliveryLocations
			: [],
		LocationType.PostBox
	);
	const parcelPointsSelectOptions = getLocationsSelectOptions(
		services &&
			services[services.findIndex(service => service.id.toLowerCase() === LocationType.ParcelPoint.toLowerCase())]
			? services[
					services.findIndex(service => service.id.toLowerCase() === LocationType.ParcelPoint.toLowerCase())
			  ].deliveryLocations
			: [],
		LocationType.ParcelPoint
	);
	const calcTotalPrice = (servicePrice, id) => {
		let extraServicesTotal = 0;

		services.forEach(service => {
			if (service.id === id) {
				service.extraServices.forEach(extraService => {
					if (extraService.blocked && extraService.requirements === 'RequiresAuthentication') return;
					if (values.optionalServices.includes(extraService.id)) {
						if (extraService.id === OPTIONAL_SERVICE_COD)
							extraServicesTotal += Math.max(extraService.priceAmount, codOptionalServiceCustomPrice);
						else if (extraService.id === OPTIONAL_SERVICE_INSURANCE)
							extraServicesTotal += Math.max(
								extraService.priceAmount,
								insuranceOptionalServiceCustomPrice
							);
						else extraServicesTotal += extraService.priceAmount;
					}
				});
			}
		});
		const totalPrice = servicePrice + extraServicesTotal;

		return totalPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
	};

	useEffect(() => {
		if (infoFormInfo.country?.value !== 'US') return;
		if (!values.optionalServices || values.optionalServices.length === 0) return;

		setFieldValue('optionalServices', []);
	}, [infoFormInfo.country?.value, values.optionalServices, setFieldValue]);

	return (
		<div>
			{loading && [...Array(3)].map((_key, index) => <FormikRadioTileLoader key={index} />)}
			<FormikFieldWrapper name="serviceOption" languageComponent="registerShipment">
				{services &&
					services.map(service => (
						<StyledService key={service.id}>
							{service.id !== 'Postbox' || (service.id === 'Postbox' && parcelCount === 1) ? (
								<FormikRadioTile
									id={service.id}
									name="serviceOption"
									icon={getIconByServiceCode(service.id)}
									label={service.labels[getCurrentLangPretty() === 'is' ? 0 : 1]?.title}
									price={
										calcTotalPrice(service.priceAmount, service.id) +
										(getCurrentLangPretty() === 'is' ? ' kr.' : ' ISK')
									}
									estimatedPrice
									priceSubLabel={weightCategoryLabel}
									active={values.serviceOption === service.id}
									setFieldValue={setFieldValue}
									compact>
									<StyledDescription>
										{service.labels[getCurrentLangPretty() === 'is' ? 0 : 1]?.description}
									</StyledDescription>
									{service.id === 'Postbox' && (
										<FormikFieldWrapper
											name="serviceOptionPostbox"
											languageComponent="registerShipment"
											marginTop="2rem">
											<FormikSelect
												id="serviceOptionPostbox"
												name="serviceOptionPostbox"
												value={values.serviceOptionPostbox}
												options={postBoxesSelectOptions}
												onBlur={setFieldTouched}
												placeholder={`${t('step-shipment.choose-postbox')}`}
												onChange={setFieldValue}
											/>
										</FormikFieldWrapper>
									)}
									{service.id === 'ParcelPoint' && (
										<FormikFieldWrapper
											name="serviceOptionParcelpoint"
											languageComponent="registerShipment"
											marginTop="2rem">
											<FormikSelect
												id="serviceOptionParcelpoint"
												name="serviceOptionParcelpoint"
												value={values.serviceOptionParcelpoints}
												options={parcelPointsSelectOptions}
												onBlur={setFieldTouched}
												placeholder={`${t('step-shipment.choose-parcelpoint')}`}
												onChange={setFieldValue}
											/>
										</FormikFieldWrapper>
									)}
									{['Postbox', 'ParcelPoint'].includes(service.id) &&
										infoFormInfo.receiverPhone === '' && (
											<FormikFieldWrapper
												name="receiverPhone"
												label={`${t('step-info.receiverPhone')}*`}
												smallLabel
												languageComponent="registerShipment"
												marginTop={'2rem'}>
												<PhoneCountryInput
													id="receiverPhone"
													name="receiverPhone"
													value={values.receiverPhone}
													countryCodeName="receiverPhoneCountryCode"
													countryCodeValue={values.receiverPhoneCountryCode}
													countries={countries}
													onBlur={setFieldTouched}
													setFieldValue={setFieldValue}
													defaultCountryCode="IS"
													placeholder={t('step-info.receiverPhone')}
													searchPlaceholder={t('step-info.phone-country-search')}
													autoComplete="tel"
												/>
											</FormikFieldWrapper>
										)}
									{service.extraServices.length > 0 && infoFormInfo.country.value !== 'US' && (
										<OptionalServices>
											<div>{t('shared.optional-services') as React.ReactNode}</div>
											<FormikFieldWrapper
												name="optionalServices"
												languageComponent="registerShipment">
												{sortOptionalServices(service.extraServices).map((extraService, index) => (
													<>
														<FormikCheckboxTileGroup
															key={index}
															id="optionalServices"
															onBlur={setFieldTouched}
															onChange={setFieldValue}
															value={values.optionalServices}>
															<SimpleFormikCheckboxTile
																values={values.optionalServices}
																id={extraService.id}
																label={
																	extraService.labels[
																		getCurrentLangPretty() === 'is' ? 0 : 1
																	]?.title
																}
																setFieldValue={setFieldValue}
																name={
																	extraService.labels[
																		getCurrentLangPretty() === 'is' ? 0 : 1
																	]?.title
																}
																price={
																	(extraService.id === OPTIONAL_SERVICE_INSURANCE
																		? Math.max(
																				insuranceOptionalServiceCustomPrice,
																				extraService.priceAmount
																		  )
																		: extraService.id === OPTIONAL_SERVICE_COD
																		? Math.max(
																				codOptionalServiceCustomPrice,
																				extraService.priceAmount
																		  )
																		: extraService.priceAmount) +
																	(getCurrentLangPretty() === 'is' ? ' kr.' : ' ISK')
																}
																disabled={extraService.blocked === true && extraService.requirements === 'RequiresAuthentication'}
																disabledMessage={undefined}
																active={
																	!(extraService.blocked && extraService.requirements === 'RequiresAuthentication') &&
																	values.optionalServices.includes(extraService.id)
																}>
																{extraService.blocked && extraService.requirements === 'RequiresAuthentication' && (
																	<BlockedMessage>
																		<Trans
																			t={t}
																			i18nKey="step-shipment.requires-login"
																			components={[
																				<A
																					href={
																						getCurrentLangPretty() === 'is'
																							? '/minar-sidur'
																							: '/en/my-account'
																					}
																				/>,
																			]}
																		/>
																	</BlockedMessage>
																)}
																<StyledDescription>
																	{extraService.labels[
																		getCurrentLangPretty() === 'is' ? 0 : 1
																	]?.description || ''}
																</StyledDescription>
																{service.id === 'Postbox' &&
																	extraService.id === 'RecipientPays' &&
																	values.optionalServices.includes(
																		'RecipientPays'
																	) && (
																		<RedP>
																			{t('step-shipment.recipientPaysPostbox')}
																		</RedP>
																	)}
																{extraService.id === 'RecipientPays' && (
																	<RecipientPaysSocialIdPrompt
																		setFieldValue={setFieldValue}
																		selected={values.optionalServices.includes(
																			'RecipientPays'
																		)}
																	/>
																)}
															</SimpleFormikCheckboxTile>
														</FormikCheckboxTileGroup>
														{extraService.id === OPTIONAL_SERVICE_INSURANCE && (
															<NestedCheckboxWrapper>
																<FormikFieldWrapper
																	name={'insuranceTerms'}
																	languageComponent={'registerShipment'}>
																	<Columns>
																		<StyledColumn>
																			<FormikCheckbox
																				id={'insuranceTerms'}
																				name={'insuranceTerms'}
																				label={
																					<Trans
																						t={t}
																						i18nKey={
																							'step-info.insuranceTerms'
																						}
																						components={[
																							<A
																								href={
																									getCurrentLangPretty() ===
																									'is'
																										? '/posturinn/upplysingar/vidskiptaskilmalar'
																										: '/en/iceland-post/information/terms-conditions'
																								}
																								target="_blank"
																							/>,
																						]}
																					/>
																				}
																				setFieldValue={setFieldValue}
																			/>
																		</StyledColumn>
																		<Column tablet={1 / 2}>
																			{services &&
																				values.optionalServices.includes(
																					OPTIONAL_SERVICE_INSURANCE
																				) &&
																				extraService.id ===
																					OPTIONAL_SERVICE_INSURANCE && (
																					<OptionalServicesForms>
																						<InsuranceForm
																							value={
																								values.insuranceAmount
																							}
																						/>
																					</OptionalServicesForms>
																				)}
																		</Column>
																	</Columns>
																</FormikFieldWrapper>
															</NestedCheckboxWrapper>
														)}
													</>
												))}
											</FormikFieldWrapper>
										</OptionalServices>
									)}

									{services && values.optionalServices.includes(OPTIONAL_SERVICE_COD) && (
										<OptionalServicesForms>
											<CodForm amount={values.codAmount} />
										</OptionalServicesForms>
									)}
								</FormikRadioTile>
							) : (
								<FormikRadioTile
									id={service.id}
									name="serviceOption"
									icon={getIconByServiceCode(service.id)}
									label={service.labels[getCurrentLangPretty() === 'is' ? 0 : 1]?.title}
									price={
										calcTotalPrice(service.priceAmount, service.id) +
										(getCurrentLangPretty() === 'is' ? ' kr.' : ' ISK')
									}
									estimatedPrice
									active={values.serviceOption === service.id}
									setFieldValue={null}
									compact
									disabled>
									<StyledDescription>{`${t('step-shipment.postbox-unavailable')}`}</StyledDescription>
								</FormikRadioTile>
							)}
						</StyledService>
					))}
			</FormikFieldWrapper>
			{services && services.length === 0 && !loading && (
				<StyledNoServicesFound>{`${t('step-shipment.no-services')}`}</StyledNoServicesFound>
			)}
		</div>
	);
};

export default Services;

const NestedCheckboxWrapper = styled.div`
	margin-top: -1rem;
	margin-bottom: 1rem;
	background-color: ${({ theme }) => theme.colors.grayLighter};
	width: 100%;
	padding: 2rem 1rem 1rem;
	> * {
		display: flex;
		justify-content: flex-end;
	}
`;

const RedP = styled.p`
	color: ${({ theme }) => theme.colors.primaryLight};
`;

const StyledColumn = styled(Column)`
	display: flex;
	align-items: center;
	justify-content: flex-end;
`;

const BlockedMessage = styled.div`
	font-size: 1.3rem;
	color: red !important;
	margin-top: 0.5rem;
	font-style: italic;

	a {
		color: red !important;
		text-decoration: underline;
	}
`;
